import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch, detailMessage } from '../api';
import SkillsTable from '../components/SkillsTable';
import { BROWSER_ENGINES, RESOURCE_DEFAULTS, QUOTA_DEFAULTS } from '../components/UserForm';
import { Badge, Icon, PageHeader, Switch } from '../components/ui';

const TIMEZONES = [
  'UTC', 'Europe/Rome', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'America/New_York', 'America/Los_Angeles', 'America/Chicago',
  'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney',
];

const EXEC_MODES = [
  { value: 'full', label: 'Full (no restrictions)' },
  { value: 'denylist', label: 'Denylist (block dangerous commands)' },
  { value: 'allowlist', label: 'Allowlist (only safe commands)' },
];

// Tiers are capped by a monthly cost budget. This label used to read `daily_token_limit`,
// a field the tier API no longer returns, so every tier was offered as "unlimited". Same
// format as the Edit user page, so a tier reads the same in both places.
const formatTierLabel = (tier) => {
  const cap = tier.monthly_cost_limit
    ? `$${parseFloat(tier.monthly_cost_limit).toFixed(2)}/month cap`
    : 'unlimited';
  return `${tier.name}, ${cap}`;
};

const amount = (value, unit) => (
  value === null || value === undefined || value === '' ? null : `${value} ${unit}`
);

// One line saying what the collapsed section holds, so leaving it closed is an informed
// choice rather than a blind one.
const resourceSummary = (f) => [
  `${amount(f.cpu_reserved, 'vCPU') || 'no CPU'} and ${amount(f.memory_reserved_gb, 'GB') || 'no memory'} reserved`,
  amount(f.cpu_limit, 'vCPU cap') || 'protected CPU cap (0.5 vCPU minimum)',
  amount(f.memory_limit_gb, 'GB memory cap') || 'no memory cap',
  amount(f.memory_swap_gb, 'GB swap') || 'no swap',
  `crons at least ${f.cron_min_interval ?? 60} min apart, up to ${f.cron_max_jobs ?? 3}`,
  `${f.max_concurrent_subagents ?? 3} sub-agents`,
  `${f.exec_mode || 'full'} exec`,
  `${f.browser_engine || 'lightpanda'} browser`,
  f.timezone || 'UTC',
].join(' · ');

// One channel: its switch, and the address it needs revealed underneath while it is on.
function ChannelRow({ id, title, description, checked, onChange, children }) {
  return (
    <div className="channel-item">
      <div className="setting-row p-0">
        <div className="min-w-0">
          <label className="setting-row-title" htmlFor={id}>{title}</label>
          <p className="setting-row-desc">{description}</p>
        </div>
        <Switch id={id} checked={checked} onChange={onChange} />
      </div>
      {children}
    </div>
  );
}

function ResourceFields({ formData, onChange }) {
  return (
    <>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="cu-cpu-reserved">CPU reserved (vCPU)</label>
          <input name="cpu_reserved" id="cu-cpu-reserved" type="number" step="0.1" min="0" className="form-input" value={formData.cpu_reserved ?? ''} onChange={onChange} placeholder="Platform default" />
          <div className="form-hint">Soft reservation the scheduler packs agents by. Empty uses the platform default.</div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="cu-memory-reserved">Memory reserved (GB)</label>
          <input name="memory_reserved_gb" id="cu-memory-reserved" type="number" step="0.1" min="0" className="form-input" value={formData.memory_reserved_gb ?? ''} onChange={onChange} placeholder="Platform default" />
          <div className="form-hint">Soft reservation the scheduler packs agents by. Empty uses the platform default.</div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="cu-cpu-limit">CPU limit (vCPU)</label>
          <input name="cpu_limit" id="cu-cpu-limit" type="number" step="0.1" min="0.5" className="form-input" value={formData.cpu_limit ?? ''} onChange={onChange} placeholder="0.5 default" />
          <div className="form-hint">Hard cap. Empty uses the protected platform ceiling, at least 0.5 vCPU and never below the reservation.</div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="cu-memory-limit">Memory limit (GB)</label>
          <input name="memory_limit_gb" id="cu-memory-limit" type="number" step="0.1" min="0.1" className="form-input" value={formData.memory_limit_gb ?? ''} onChange={onChange} placeholder="Platform default" />
          <div className="form-hint">Hard cap, OOM-killed if exceeded. Empty uses the platform default.</div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="cu-memory-swap">Memory swap (GB)</label>
          <input name="memory_swap_gb" id="cu-memory-swap" type="number" step="0.5" className="form-input" value={formData.memory_swap_gb ?? ''} onChange={onChange} placeholder="0" />
          <div className="form-hint">Disk swap space for overflow memory.</div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="cu-browser-engine">Browser engine</label>
          <select name="browser_engine" id="cu-browser-engine" className="form-input" value={formData.browser_engine || 'lightpanda'} onChange={onChange}>
            {BROWSER_ENGINES.map(engine => <option key={engine.value} value={engine.value}>{engine.label}</option>)}
          </select>
          <div className="form-hint">Chromium handles modern SPAs and native screenshots. For sustained browser work, explicitly raise both CPU reserved and CPU limit, typically to 2 vCPU.</div>
        </div>
      </div>
    </>
  );
}

function QuotaFields({ formData, onChange }) {
  // A timezone set through the API can be one this short list does not offer. Listing it
  // keeps the select showing the value that will actually be sent.
  const timezones = !formData.timezone || TIMEZONES.includes(formData.timezone)
    ? TIMEZONES
    : [formData.timezone, ...TIMEZONES];
  return (
    <>
      <div className="form-row cols-3">
        <div className="form-group">
          <label className="form-label" htmlFor="cu-cron-interval">Minimum cron interval (min)</label>
          <input name="cron_min_interval" id="cu-cron-interval" type="number" className="form-input" value={formData.cron_min_interval ?? ''} onChange={onChange} min="60" max="1440" />
          <div className="form-hint">60 to 1440 minutes.</div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="cu-cron-jobs">Max cron jobs</label>
          <input name="cron_max_jobs" id="cu-cron-jobs" type="number" className="form-input" value={formData.cron_max_jobs ?? ''} onChange={onChange} min="1" max="20" />
          <div className="form-hint">1 to 20.</div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="cu-subagents">Max sub-agents</label>
          <input name="max_concurrent_subagents" id="cu-subagents" type="number" className="form-input" value={formData.max_concurrent_subagents ?? ''} onChange={onChange} min="1" max="10" />
          <div className="form-hint">Running at the same time, 1 to 10.</div>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="cu-timezone">Timezone</label>
          <select name="timezone" id="cu-timezone" className="form-input" value={formData.timezone} onChange={onChange}>
            {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="cu-exec-mode">Exec mode</label>
          <select name="exec_mode" id="cu-exec-mode" className="form-input" value={formData.exec_mode} onChange={onChange}>
            {EXEC_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <div className="form-hint">Controls which shell commands the agent can execute.</div>
        </div>
      </div>
    </>
  );
}

// Save bar for a form built from several cards. `.form-actions.sticky` is made to sit
// INSIDE one card (its negative margins reach that card's edges); as the last child of the
// form it stays in view down the whole page, so it takes a card's full outline instead.
function SaveBar({ children }) {
  return (
    <div className="card form-actions sticky m-0" style={{ borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-md)' }}>
      {children}
    </div>
  );
}

function CreateUser() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [telegramEnabled, setTelegramEnabled] = useState(true);
  // Off by default: Telegram is the usual case and RossHub pairing normally happens when
  // the person Syncs, so an operator opts in only when they already know the member ID.
  const [websiteEnabled, setWebsiteEnabled] = useState(false);
  // Teams, same reasoning. Off by default because the usual path is the person pairing
  // themselves with a token or coupon — one bot serves the whole tenant, so there is
  // nothing per-user to hand out and no reason for an operator to pre-enter anything
  // unless they already have the Object ID in front of them.
  const [teamsEnabled, setTeamsEnabled] = useState(false);
  // Whether the deployment serves Telegram at all. A deployment with no bots in the pool
  // cannot receive Telegram messages (the bridge polls per pool bot), so the toggle starts
  // ON and is switched off once the empty pool comes back — otherwise an operator there
  // would have to turn Telegram off and supply a Telegram User ID it will never use, on
  // every single create. Left switchable, since the answer changes the moment a bot is added.
  const [botPoolEmpty, setBotPoolEmpty] = useState(false);
  const [useCustomBot, setUseCustomBot] = useState(false);
  const [enabledSkills, setEnabledSkills] = useState([]);
  const [enabledApps, setEnabledApps] = useState([]);
  // Resources and quotas are rarely changed at creation, so they start folded away.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advancedRef = useRef(null);
  // Inline messages for the checks handleSubmit makes, keyed by field ('channels' for the
  // at-least-one rule). Each is cleared as soon as its field is edited.
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    telegram_id: '',
    website_id: '',
    teams_id: '',
    telegram_bot_token: '',
    ...RESOURCE_DEFAULTS,
    tier_id: '',
    ...QUOTA_DEFAULTS,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tiersResp, skillsResp, defaultsResp, botsResp] = await Promise.all([
          apiFetch('/api/tiers'),
          apiFetch('/api/skills'),
          apiFetch('/api/user-defaults'),
          apiFetch('/api/telegram-bots'),
        ]);
        if (tiersResp.ok) setTiers(await tiersResp.json());

        if (botsResp.ok) {
          const bots = await botsResp.json();
          if (Array.isArray(bots) && bots.length === 0) {
            setBotPoolEmpty(true);
            setTelegramEnabled(false);
          }
        }

        if (skillsResp.ok) {
          const skillsData = await skillsResp.json();
          // The RossGate services and MCP servers set on for new users under Tools, MCP are
          // pre-ticked too; what the admin submits is what the user gets. The same union as
          // SkillsTable's own seed: without the MCP servers here, a server given to new users
          // arrived unticked, and so was revoked, whenever this fetch finished last.
          if (skillsData.default_enabled) {
            setEnabledSkills([
              ...skillsData.default_enabled,
              ...(skillsData.rossgate_default_enabled || []),
              ...(skillsData.mcp_default_enabled || []),
            ]);
          }
        }

        const appsResp = await apiFetch('/api/apps');
        if (appsResp.ok) {
          const appsData = await appsResp.json();
          if (appsData.default_enabled) setEnabledApps(appsData.default_enabled);
        }

        if (defaultsResp.ok) {
          const apiDefaults = await defaultsResp.json();
          // Every resource and quota field the backend answers with, not just the two
          // limits. Applying a subset left the rest on the local fallbacks, which had
          // drifted from defaults.py — see the note in formDefaults.js.
          const fields = [
            'cpu_reserved', 'memory_reserved_gb', 'cpu_limit', 'memory_limit_gb',
            'memory_swap_gb', 'cron_min_interval', 'cron_max_jobs',
            'max_concurrent_subagents', 'exec_mode', 'browser_engine', 'timezone',
          ];
          setFormData(prev => {
            const next = { ...prev };
            fields.forEach(f => { if (f in apiDefaults) next[f] = apiDefaults[f] ?? null; });
            return next;
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const clearErrors = (...keys) => setErrors(prev => {
    if (!keys.some(k => prev[k])) return prev;
    const next = { ...prev };
    keys.forEach(k => { delete next[k]; });
    return next;
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let finalValue = value;
    if (type === 'number') {
      finalValue = value === '' ? null : parseFloat(value);
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    clearErrors(name);
  };

  // Folding the section unmounts its fields, and an unmounted field is not validated on
  // submit, so an out-of-range value could otherwise be tucked away and sent. Report it
  // instead of hiding it.
  const toggleAdvanced = () => {
    if (advancedOpen) {
      const invalid = advancedRef.current?.querySelector(':invalid');
      if (invalid) {
        invalid.reportValidity();
        return;
      }
    }
    setAdvancedOpen(open => !open);
  };

  // Marks the field that stopped the save, moves focus to it and releases the button.
  // The toast each caller raises is kept: it is what the page has always said.
  const flag = (field, message, focusId) => {
    setErrors({ [field]: message });
    setLoading(false);
    document.getElementById(focusId)?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // At least one address, on any channel. This is the backend's CREATION rule, mirrored
    // here so the form says so before the round trip: a user created with no address is
    // unreachable on every channel and cannot be paired by the trigger. Telegram is no
    // longer the only option — a RossHub member ID alone is a complete pairing.
    // (Editing is different: there, unpairing everything is allowed.)
    if (!telegramEnabled && !websiteEnabled && !teamsEnabled) {
      flag('channels', 'Turn on at least one channel so the agent can be reached.', 'cu-ch-telegram');
      toast.error('Enable at least one channel: Telegram, RossHub, or Microsoft Teams');
      return;
    }
    if (websiteEnabled && !(formData.website_id || '').trim()) {
      flag('website_id', 'Enter the member ID, or turn RossHub off.', 'cu-website-id');
      toast.error('RossHub Member ID is required when RossHub is enabled');
      return;
    }
    if (teamsEnabled && !(formData.teams_id || '').trim()) {
      flag('teams_id', 'Enter the Object ID, or turn Microsoft Teams off.', 'cu-teams-id');
      toast.error('Entra Object ID is required when Microsoft Teams is enabled');
      return;
    }
    // Caught here as well as in the backend because it is THE mistake this field invites,
    // and a round trip to be told "an email cannot be paired" reads as the form being
    // broken rather than as the address being the wrong kind.
    if (teamsEnabled && (formData.teams_id || '').includes('@')) {
      flag('teams_id', 'That is an email address. A Teams message carries the sender\'s Object ID, not their email.', 'cu-teams-id');
      toast.error('A Teams message does not carry the sender\'s email. Use their Entra Object ID.');
      return;
    }
    if (telegramEnabled && !formData.telegram_id) {
      flag('telegram_id', 'Enter the Telegram user ID, or turn Telegram off.', 'cu-telegram-id');
      toast.error('Telegram User ID is required when Telegram is enabled');
      return;
    }
    if (telegramEnabled && useCustomBot && !formData.telegram_bot_token) {
      flag('telegram_bot_token', 'Enter the bot token, or use the shared bot.', 'cu-bot-token');
      toast.error('Bot Token is required when using a custom bot');
      return;
    }
    setErrors({});

    try {
      const data = { ...formData };
      if (!data.email) delete data.email;
      if (!telegramEnabled) { delete data.telegram_id; delete data.telegram_bot_token; }
      if (telegramEnabled && !useCustomBot) delete data.telegram_bot_token;
      // Omitted rather than sent empty. Empty means "unpair" on the update endpoint, and
      // sending it here would be a pointless write on a user who has no pairing yet.
      if (!websiteEnabled) delete data.website_id;
      if (!data.website_id) delete data.website_id;
      if (!teamsEnabled) delete data.teams_id;
      if (!data.teams_id) delete data.teams_id;
      data.tier_id = formData.tier_id ? parseInt(formData.tier_id) : null;
      data.cron_min_interval = parseInt(formData.cron_min_interval) || 60;
      data.cron_max_jobs = parseInt(formData.cron_max_jobs) || 3;
      data.max_concurrent_subagents = parseInt(formData.max_concurrent_subagents) || 3;
      data.exec_mode = formData.exec_mode || 'full';
      data.timezone = formData.timezone || 'UTC';
      data.enabled_tools = enabledSkills;
      data.enabled_apps = enabledApps;
      // Provider/model AND custom API keys live on the tier — nothing more to send here.

      const response = await apiFetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`User ${result.username} is being built, check status on the users page`);
        navigate('/users');
      } else {
        // detailMessage, not the raw detail: a 422 carries an ARRAY of objects, which a
        // toast cannot render.
        const error = await response.json().catch(() => ({}));
        toast.error(detailMessage(error.detail, 'Failed to create user'));
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const fieldError = (key) => errors[key] && (
    <div className="form-error" role="alert">{errors[key]}</div>
  );

  return (
    <div>
      <PageHeader
        eyebrow={<Link to="/users" className="row gap-1"><Icon name="arrowLeft" size={14} />Users</Link>}
        description="Creates the account, assigns a bot and provisions the agent's workspace."
      >
        <h1 className="page-title">New user</h1>
      </PageHeader>

      <form onSubmit={handleSubmit}>
        <section className="card" aria-labelledby="cu-account-title">
          <div className="card-header">
            <div>
              <h2 className="card-title" id="cu-account-title">Account</h2>
              <p className="card-subtitle">Who the agent belongs to.</p>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="cu-username">
                Username<span className="required" aria-hidden="true">*</span>
              </label>
              {/* Mirrors services/users.USERNAME_RE exactly (a test compares the two). The
                  hyphen is escaped because browsers compile `pattern` with the `v` flag,
                  which refuses an unescaped '-' in a character class. */}
              <input name="username" id="cu-username" type="text" className="form-input" value={formData.username} onChange={handleChange} required maxLength={100} pattern="[a-z0-9][a-z0-9._+@\-]{0,98}[a-z0-9]" title="2-100 characters: lowercase letters, digits and . _ + @ -, starting and ending with a letter or digit" aria-describedby="cu-username-hint" autoComplete="off" spellCheck={false} autoFocus />
              <div className="form-hint" id="cu-username-hint">
                2-100 characters: lowercase letters, digits and . _ + @ -. An email address works. Becomes part of the agent&apos;s Kubernetes object names.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="cu-email">Email</label>
              <input name="email" id="cu-email" type="email" className="form-input" value={formData.email} onChange={handleChange} autoComplete="off" />
              <div className="form-hint">Optional.</div>
            </div>
          </div>
        </section>

        <section className="card" aria-labelledby="cu-channels-title">
          <div className="card-header">
            <div>
              <h2 className="card-title" id="cu-channels-title">Channels</h2>
              <p className="card-subtitle">
                At least one is required. Each address pairs its channel now; the rest can be paired later.
              </p>
            </div>
          </div>
          {errors.channels && (
            <div className="notice notice-danger" role="alert">
              <Icon name="alertCircle" />
              <div className="notice-body">{errors.channels}</div>
            </div>
          )}

          <ChannelRow
            id="cu-ch-telegram"
            title="Telegram"
            description="Pair the person's Telegram account and choose the bot that serves it."
            checked={telegramEnabled}
            onChange={on => { setTelegramEnabled(on); clearErrors('channels', 'telegram_id', 'telegram_bot_token'); }}
          >
            {botPoolEmpty && !telegramEnabled && (
              <p className="form-hint">
                No bots in the pool, so Telegram messages cannot be received. Add one on
                the <Link to="/channels">Channels page</Link> to use this channel.
              </p>
            )}
            {telegramEnabled && (
              <div className="card-inset mt-3">
                {botPoolEmpty && !useCustomBot && (
                  <div className="notice notice-warn">
                    <Icon name="alert" />
                    <div className="notice-body">
                      The shared pool has no bots. This user will be created without one and
                      will not receive Telegram messages until a bot is added on
                      the <Link to="/channels">Channels page</Link>, or a custom bot is set below.
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label" htmlFor="cu-telegram-id">
                    Telegram user ID<span className="required" aria-hidden="true">*</span>
                  </label>
                  <input name="telegram_id" id="cu-telegram-id" type="text" className="form-input" value={formData.telegram_id} onChange={handleChange} required={telegramEnabled} placeholder="123456789" inputMode="numeric" autoComplete="off" aria-invalid={errors.telegram_id ? true : undefined} aria-describedby="cu-telegram-id-hint" />
                  {fieldError('telegram_id')}
                  <div className="form-hint" id="cu-telegram-id-hint">Get it from @userinfobot on Telegram.</div>
                </div>
                <div className="form-group mb-0" role="radiogroup" aria-labelledby="cu-bot-label">
                  <div className="form-label" id="cu-bot-label">Bot</div>
                  <div className="row-wrap gap-4">
                    <label className="check-row">
                      <input type="radio" name="telegram_bot_choice" checked={!useCustomBot} onChange={() => { setUseCustomBot(false); clearErrors('telegram_bot_token'); }} />
                      Shared (default)
                    </label>
                    <label className="check-row">
                      <input type="radio" name="telegram_bot_choice" checked={useCustomBot} onChange={() => setUseCustomBot(true)} />
                      Custom bot
                    </label>
                  </div>
                  {!useCustomBot && (
                    <div className="form-hint">Messages are routed through the platform&apos;s shared bot.</div>
                  )}
                  {useCustomBot && (
                    <div className="mt-3">
                      <label className="form-label" htmlFor="cu-bot-token">
                        Bot token<span className="required" aria-hidden="true">*</span>
                      </label>
                      <input name="telegram_bot_token" id="cu-bot-token" type="text" className="form-input mono" value={formData.telegram_bot_token} onChange={handleChange} required={useCustomBot} placeholder="123456:ABC-DEF…" autoComplete="off" spellCheck={false} aria-invalid={errors.telegram_bot_token ? true : undefined} />
                      {fieldError('telegram_bot_token')}
                      <div className="form-hint">Get it from @BotFather on Telegram.</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </ChannelRow>

          {/* RossHub, toggled like Telegram. There is no bot to configure here — one
            * API, one credential — so the address is the whole configuration. Leave the
            * channel off and the person pairs it themselves with Sync in RossHub. */}
          <ChannelRow
            id="cu-ch-website"
            title="RossHub"
            description="Pair a RossHub member now. Left off, the person pairs it themselves with Sync in RossHub."
            checked={websiteEnabled}
            onChange={on => { setWebsiteEnabled(on); clearErrors('channels', 'website_id'); }}
          >
            {websiteEnabled && (
              <div className="card-inset mt-3">
                <div className="form-group mb-0">
                  <label className="form-label" htmlFor="cu-website-id">
                    RossHub member ID<span className="required" aria-hidden="true">*</span>
                  </label>
                  <input
                    name="website_id"
                    id="cu-website-id"
                    type="text"
                    className="form-input"
                    value={formData.website_id}
                    onChange={handleChange}
                    required={websiteEnabled}
                    placeholder="4820193746"
                    inputMode="numeric"
                    autoComplete="off"
                    aria-invalid={errors.website_id ? true : undefined}
                    aria-describedby="cu-website-id-hint"
                  />
                  {fieldError('website_id')}
                  <div className="form-hint" id="cu-website-id-hint">
                    The 10-digit member ID from RossHub, shown on the user&apos;s profile and in the admin Users list.
                  </div>
                </div>
              </div>
            )}
          </ChannelRow>

          {/* Microsoft Teams. One Azure Bot serves an entire tenant, so unlike Telegram
            * there is no per-user credential and nothing to hand out — this address is
            * the ONLY thing that says who is texting. It has to be the Entra object id,
            * because that is what arrives on the activity; an email cannot be matched
            * without Graph. See services/teams_apps.normalize_teams_user_id. */}
          <ChannelRow
            id="cu-ch-teams"
            title="Microsoft Teams"
            description="Left off, the person pairs themselves by sending their activation token or coupon code to the bot, which is the easier route for more than a handful of people."
            checked={teamsEnabled}
            onChange={on => { setTeamsEnabled(on); clearErrors('channels', 'teams_id'); }}
          >
            {teamsEnabled && (
              <div className="card-inset mt-3">
                <div className="form-group mb-0">
                  <label className="form-label" htmlFor="cu-teams-id">
                    Entra Object ID<span className="required" aria-hidden="true">*</span>
                  </label>
                  <input
                    name="teams_id"
                    id="cu-teams-id"
                    type="text"
                    className="form-input mono"
                    value={formData.teams_id}
                    onChange={handleChange}
                    required={teamsEnabled}
                    placeholder="3f2504e0-4f89-11d3-9a0c-0305e82c3301"
                    autoComplete="off"
                    spellCheck={false}
                    aria-invalid={errors.teams_id ? true : undefined}
                    aria-describedby="cu-teams-id-hint"
                  />
                  {fieldError('teams_id')}
                  <div className="form-hint" id="cu-teams-id-hint">
                    From the Microsoft Entra admin center: Users &rarr; the person &rarr; <b>Object ID</b>.
                    Not their email, a Teams message does not carry one.
                  </div>
                </div>
              </div>
            )}
          </ChannelRow>
        </section>

        <section className="card" aria-labelledby="cu-tier-title">
          <div className="card-header">
            <div>
              <h2 className="card-title" id="cu-tier-title">Tier</h2>
              <p className="card-subtitle">The tier sets the agent&apos;s models and its monthly budget.</p>
            </div>
          </div>
          <div className="form-group mb-0" style={{ maxWidth: 480 }}>
            <label className="form-label" htmlFor="cu-tier">Usage tier</label>
            <select name="tier_id" id="cu-tier" className="form-input" value={formData.tier_id} onChange={handleChange}>
              <option value="">No tier (unlimited)</option>
              {tiers.map(t => (
                <option key={t.id} value={t.id}>{formatTierLabel(t)}</option>
              ))}
            </select>
            <div className="form-hint">
              Once the main budget is spent, the tier&apos;s backup model takes over if it has one.
            </div>
          </div>
        </section>

        <section className="card" aria-labelledby="cu-tools-title">
          <div className="card-header">
            <div>
              <h2 className="card-title" id="cu-tools-title">Tools</h2>
              <p className="card-subtitle">What the agent can use. The platform defaults arrive switched on.</p>
            </div>
          </div>
          <SkillsTable
            enabledSkills={enabledSkills}
            enabledApps={enabledApps}
            onToggleSkill={(id, on) => setEnabledSkills(prev => on ? [...prev, id] : prev.filter(s => s !== id))}
            onToggleApp={(id, on) => setEnabledApps(prev => on ? [...prev, id] : prev.filter(s => s !== id))}
            onSetAllSkills={setEnabledSkills}
            onSetAllApps={setEnabledApps}
          />
        </section>

        {/* There was a "Ross Runtime Image" selector in this part of the form. It was
            decoration: the field was posted, `UserCreate` accepted it, and neither
            `create_user` nor the provisioner ever read it, `services/openpaw.provision_user_workspace`
            stamps `users.image` from the platform-wide registry + runtime tag on every provision,
            and `UserUpdate` has no image field at all, so there was no way to correct the
            choice afterwards either. Picking anything other than the live tag silently did
            nothing. The runtime tag is a platform-wide setting; removed rather than left
            looking operative. */}
        <section className="card" aria-labelledby="cu-advanced-title">
          <div className={advancedOpen ? 'card-header' : 'row-wrap between row-top gap-4'}>
            <div className="min-w-0">
              <h2 className="card-title" id="cu-advanced-title">
                Resources and quotas{' '}
                <Badge tone="neutral">Advanced</Badge>
              </h2>
              <p className="card-subtitle">
                {advancedOpen
                  ? 'Filled in with the platform defaults. Empty resource fields use protected platform defaults.'
                  : resourceSummary(formData)}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              aria-expanded={advancedOpen}
              aria-controls="cu-advanced"
              onClick={toggleAdvanced}
            >
              <Icon name={advancedOpen ? 'chevronUp' : 'chevronDown'} />
              {advancedOpen ? 'Hide' : 'Customise'}
            </button>
          </div>
          <div id="cu-advanced" ref={advancedRef}>
            {advancedOpen && (
              <>
                <h3 className="overline mb-3">Resources</h3>
                <ResourceFields formData={formData} onChange={handleChange} />
                <h3 className="overline mb-3 mt-2">Quotas</h3>
                <QuotaFields formData={formData} onChange={handleChange} />
              </>
            )}
          </div>
        </section>

        <SaveBar>
          <span className="text-sm text-muted grow hide-sm">
            Provisioning starts as soon as the user is created.
          </span>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/users')} disabled={loading}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <><span className="spinner-sm" /> Creating…</> : <><Icon name="userPlus" /> Create user</>}
          </button>
        </SaveBar>
      </form>
    </div>
  );
}

export default CreateUser;
